import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';

// models
import { IDeleteUser, IUserProfile, IUserProfilePublic, IUserProfileSubscription } from '../models/user-profile.model';
import { map, tap } from 'rxjs/operators';
import { CachedDataService } from './cached-data.service';
import { UserEmailAddress } from '../modules-account/components/user-details/account-details/account-details.component';

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
};

@Injectable()
export class AccountDetailsService {
  endpoint = '/api/Profile/';
  private user: IUserProfile | undefined;
  private cachedIsAdmin: boolean | null = null;
  private isAdminObservable: Observable<boolean> | null = null;

  private readonly CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

  constructor(private http: HttpClient, private cachedDataService: CachedDataService) { }

  getIsAdmin(): Observable<boolean> {
    // Return cached value if available
    if (this.cachedIsAdmin !== null) {
      return of(this.cachedIsAdmin);
    }
    
    // Return existing observable if one is in flight
    if (this.isAdminObservable) {
      return this.isAdminObservable;
    }
    
    // Make API call and cache result
    const url = this.endpoint + 'UserIsAdmin';
    this.isAdminObservable = this.http.get<boolean>(url).pipe(
      map((isAdmin: boolean) => {
        this.cachedIsAdmin = isAdmin;
        this.isAdminObservable = null; // Clear observable after completion
        return isAdmin;
      })
    );
    
    return this.isAdminObservable;
  }
  
  clearAdminCache(): void {
    this.cachedIsAdmin = null;
    this.isAdminObservable = null;
  }

  getIsTest(): Observable<boolean> {
    const url = this.endpoint + 'UserIsTest';
    return this.http.get<boolean>(url);
  }

  getBasicUsers(includeDeleted: boolean): Observable<IUserProfileSubscription[]> {
    const url = `${this.endpoint}GetUsers?includeDeleted=${includeDeleted}`;
    return this.http.get<IUserProfileSubscription[]>(url);
  }

  getUserProfileSubscription(): Observable<IUserProfileSubscription> {
    return this.cachedDataService.getOrSet<IUserProfileSubscription>(
      'account.getUserProfileSubscription',
      () => this.http.get<IUserProfileSubscription>(this.endpoint + 'GetUserProfileSubscription'),
      this.CACHE_TTL_MS
    );
  }

  clearUserProfileSubscriptionCache(): void {
    this.cachedDataService.clear('account.getUserProfileSubscription');
  }

  // Note: the email is not used in the method, just for logging
  loadCurrentUser(): Observable<IUserProfile> {
    return this.cachedDataService.getOrSet<IUserProfile>(
      'account.loadCurrentUser',
      () => {
        const url = this.endpoint + 'GetCurrentUserProfile';
        return this.http.get<IUserProfile>(url).pipe(
          tap((user: IUserProfile) => {
            this.user = user;
          })
        );
      },
      this.CACHE_TTL_MS
    );
  }

  clearCurrentUserCache(): void {
    this.cachedDataService.clear('account.loadCurrentUser');
  }

  update(profile: IUserProfile): Observable<IUserProfile> {
    return this.http.put<IUserProfile>(this.endpoint, profile, httpOptions).pipe(
      tap(() => this.clearUserProfileSubscriptionCache())
    );
  }

  updateTrialStatus(basicUser: IUserProfileSubscription): Observable<IUserProfileSubscription> {
    const url = this.endpoint + 'UpdateTrialStatus';
    const jsonBody = JSON.stringify(basicUser);
    console.log(`*** The profile is being updated with ${jsonBody}`);

    return this.http.post<IUserProfileSubscription>(url, jsonBody, httpOptions).pipe(
      tap(() => this.clearUserProfileSubscriptionCache())
    );
  }

  deleteUser(basicUser: IUserProfileSubscription, toBeDeleted: boolean, reason: string): Observable<IDeleteUser> {
    const url = this.endpoint + 'DeleteUser';
    const body = {
      userId: basicUser.id,
      deleted: toBeDeleted,
      reason: reason,
    };

    return this.http.post<IDeleteUser>(url, body, httpOptions).pipe(
      tap(() => this.clearUserProfileSubscriptionCache())
    );
  }

  updateBasicUserRole(basicUser: IUserProfileSubscription): Observable<IUserProfileSubscription> {
    const url = this.endpoint + 'UpdateBasicUserRole';
    const jsonBody = JSON.stringify(basicUser);
    console.log(`*** The profile is being updated with ${jsonBody}`);

    return this.http.post<IUserProfileSubscription>(url, jsonBody, httpOptions);
  }

  seedDatabase(): Observable<boolean> {
    const url = this.endpoint + 'SeedDatabase';

    return this.http.post<boolean>(url, '', httpOptions);
  }

  uploadProfilePicture(imageFile: File, imagePurpose: string): Observable<boolean> {
    const url = this.endpoint + 'UploadImage' + `?imagePurpose=${imagePurpose}`;

    const formData: FormData = new FormData();
    formData.append('fileKey', imageFile, imageFile.name);

    return this.http.post<boolean>(url, formData);
  }

  getImage(imagePurpose: string): Observable<string> {
    const url = this.endpoint + `GetImage?imgPurpose=${imagePurpose}`;
    console.log(`getting image with ${url}`);
    return this.http.get(url, { responseType: 'text' });
  }

  updateUsernames(): Observable<boolean> {
    const url = this.endpoint + 'UpdateUsernameDatabase';
    return this.http.get<boolean>(url);
  }

  privateStatusUpdate(statusUpdate: boolean): Observable<boolean> {
    const url = this.endpoint + 'UpdatePrivateStatus';
    return this.http.post<boolean>(url, statusUpdate, httpOptions);
  }

  searchForUsers(searchQuery: string): Observable<IUserProfilePublic[]> {
    const endpoint = this.endpoint + `SearchForUsers`;
    return this.http.get<IUserProfilePublic[]>(endpoint, { params: { searchQuery } });
  }

  updateEmailAddresses(emailAddresses: UserEmailAddress[]): Observable<UserEmailAddress[]> {
    const endpoint = this.endpoint + `UpdateEmailAddresses`;
    return this.http.put<UserEmailAddress[]>(endpoint, emailAddresses, httpOptions);
  }

  verifyEmailAddress(emailAddressId: number): Observable<UserEmailAddress> {
    const endpoint = this.endpoint + `VerifyEmailAddress`;
    return this.http.get<UserEmailAddress>(endpoint, { params: { emailAddressId: emailAddressId.toString() } });
  }

  isEmailAddressTaken(emailAddress: string): Observable<boolean> {
    const endpoint = this.endpoint + `IsEmailAddressAssociatedWithAnotherAccount`;
    return this.http.get<boolean>(endpoint, { params: { emailAddress } });
  }

  getPublicUrl(): string | undefined {
    if (!this.user) {
      throw new Error('User cannot be undefined at this point');
    }
    return this.user.publicUrl;
  }

  isUserValid(): boolean {
    return !(!this.user);
  }
}
